import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { getCompanyProfile } from "../../../Services/Services";

pdfMake.vfs = pdfFonts.pdfMake ? pdfFonts.pdfMake.vfs : pdfFonts.vfs;

// ─── Convert image URL to base64 safely ───
const imageToBase64 = (imageSrc) => {
  return new Promise((resolve) => {
    if (!imageSrc) {
      resolve(null);
      return;
    }
    if (typeof imageSrc === "string" && imageSrc.startsWith("data:")) {
      resolve(imageSrc);
      return;
    }
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL("image/png"));
      } catch (e) {
        console.warn("Canvas export failed:", e);
        resolve(null);
      }
    };
    img.onerror = () => {
      console.warn(`Image load failed: ${imageSrc}`);
      resolve(null);
    };
    img.src = imageSrc;
  });
};

export const handlePrintIssue = async (indent) => {
  if (!indent) return;

  let company = null;
  try {
    company = await getCompanyProfile();
  } catch (err) {
    console.warn("Could not fetch company profile:", err);
  }

  const comp = company || {};

  let logoImg = null;
  if (comp.logo_url) {
    try {
      logoImg = await imageToBase64(comp.logo_url);
    } catch (error) {
      console.warn("Company logo could not be loaded:", error);
    }
  }

  const items = Array.isArray(indent.items) ? indent.items : [];
  const totalAmount = items.reduce((sum, item) => {
    return sum + (Number(item.amount) || 0);
  }, 0);

  const tableBody = [
    [
      { text: "Sl. No.", style: "tableHeader" },
      { text: "Category", style: "tableHeader" },
      { text: "Item Description", style: "tableHeader" },
      { text: "Product Type", style: "tableHeader" },
      { text: "UOM", style: "tableHeader" },
      { text: "Stock Qty.", style: "tableHeader" },
      { text: "Issue Qty.", style: "tableHeader" },
      { text: "Rate", style: "tableHeader" },
      { text: "Amount", style: "tableHeader" },
    ],
    ...items.map((item, i) => [
      { text: i + 1, alignment: "center" },
      { text: item.category_name || item.category || "-", alignment: "center", fontSize: 8 },
      { text: item.item_description || item.item_name || "-", alignment: "left" },
      { text: item.size_name || item.size || "-", alignment: "center", fontSize: 8 },
      { text: item.uom_name || item.unit || "-", alignment: "center" },
      { text: Number(item.stock_qty ?? 0).toString(), alignment: "center" },
      { text: Number(item.issue_qty ?? 0).toString(), alignment: "center" },
      { text: Number(item.price ?? 0).toFixed(2), alignment: "right" },
      { text: Number(item.amount ?? 0).toFixed(2), alignment: "right" },
    ]),
    [
      { text: "Total", colSpan: 8, alignment: "right", bold: true, fontSize: 10, margin: [0, 2, 5, 2] },
      "", "", "", "", "", "", "",
      { text: totalAmount.toFixed(2), alignment: "right", bold: true, fontSize: 10, margin: [0, 2, 5, 2] }
    ]
  ];

  const docDefinition = {
    pageOrientation: "landscape",
    pageSize: "A4",
    pageMargins: [15, 15, 15, 20],
    defaultStyle: { fontSize: 9 },

    footer: function (currentPage, pageCount) {
      return {
        text: "Page " + currentPage + " of " + pageCount,
        alignment: "right",
        fontSize: 8,
        italics: true,
        margin: [0, 5, 20, 0],
      };
    },
    content: [
      {
        table: {
          widths: ["100%"],
          body: [
            // Row 1 - Header with logo and company details
            [
              {
                table: {
                  widths: [100, "*"],
                  body: [
                    [
                      logoImg
                        ? {
                            image: logoImg,
                            width: 90,
                            alignment: "center",
                            margin: [5, 5, 5, 5],
                          }
                        : {
                            text: comp.company_name || "Siddharth Industries",
                            bold: true,
                            fontSize: 10,
                            alignment: "center",
                            margin: [5, 15, 5, 5],
                          },
                      {
                        text: [
                          { text: `${comp.company_name || "SIDDHARTH INDUSTRIES"}\n`, bold: true, fontSize: 13 },
                          { text: `${comp.tagline ? comp.tagline + "\n" : ""}`, fontSize: 9 },
                          { text: `${comp.address || ""}\n`, fontSize: 8 },
                          { text: `Phone: ${comp.phone || ""} | Email: ${comp.email || ""}`, fontSize: 8 },
                        ],
                        alignment: "center",
                        margin: [0, 5, 0, 5],
                      },
                    ],
                    [
                      "",
                      {
                        table: {
                          widths: ["auto"],
                          body: [
                            [
                              {
                                text: "MATERIAL ISSUE SLIP",
                                alignment: "center",
                                fontSize: 10,
                                bold: true,
                                margin: [25, 2, 25, 2],
                              },
                            ],
                          ],
                        },
                        layout: {
                          hLineWidth: () => 0.5,
                          vLineWidth: () => 0.5,
                          hLineColor: () => "#000",
                          vLineColor: () => "#000",
                        },
                        alignment: "center",
                        margin: [0, 2, 0, 5],
                      },
                    ],
                  ],
                },
                layout: "noBorders",
              },
            ],

            // Row 2 - Issue metadata
            [
              {
                columns: [
                  {
                    width: "50%",
                    table: {
                      widths: ["35%", "65%"],
                      body: [
                        [
                          { text: "LABOUR / CONTRACTOR", bold: true, alignment: "left" },
                          { text: `: ${indent.labour_name || "-"}`, alignment: "left" },
                        ],
                        [
                          { text: "SLIP NO.", bold: true, alignment: "left" },
                          { text: `: ${indent.slip_no || "-"}`, alignment: "left" },
                        ],
                        [
                          { text: "ISSUE DATE", bold: true, alignment: "left" },
                          {
                            text: `: ${
                              indent.created_at
                                ? new Date(indent.created_at).toLocaleDateString("en-GB")
                                : "-"
                            }`,
                            alignment: "left",
                          },
                        ],
                      ],
                    },
                    layout: "noBorders",
                  },
                  {
                    width: "50%",
                    table: {
                      widths: ["35%", "65%"],
                      body: [
                        [
                          { text: "GOODS OUT NO.", bold: true, alignment: "left" },
                          { text: `: ${indent.go_no || "-"}`, alignment: "left", bold: true },
                        ],
                        [
                          { text: "PROJECT / MOS", bold: true, alignment: "left" },
                          { text: `: ${indent.project_name || "-"}`, alignment: "left" },
                        ],
                        [
                          { text: "ISSUED BY", bold: true, alignment: "left" },
                          { text: `: ${indent.created_by_name || "-"}`, alignment: "left" },
                        ],
                        [
                          { text: "REMARKS", bold: true, alignment: "left" },
                          { text: `: ${indent.remarks || "-"}`, alignment: "left" },
                        ],
                      ],
                    },
                    layout: "noBorders",
                  },
                ],
                columnGap: 20,
                margin: [5, 5, 5, 5],
              },
            ],

            // Row 3 - Items Table
            [
              {
                table: {
                  headerRows: 1,
                  widths: [
                    "5%",   // Sl. No.
                    "15%",  // Category
                    "30%",  // Item Description
                    "12%",  // Product Type
                    "8%",   // UOM
                    "7%",   // Stock Qty
                    "7%",   // Issue Qty
                    "8%",   // Rate
                    "8%",   // Amount
                  ],
                  body: tableBody,
                },
                layout: {
                  hLineWidth: () => 0.5,
                  vLineWidth: () => 0.5,
                  hLineColor: () => "#000",
                  vLineColor: () => "#000",
                  paddingLeft: () => 4,
                  paddingRight: () => 4,
                  paddingTop: () => 3,
                  paddingBottom: () => 3,
                },
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 1,
          vLineWidth: () => 1,
          hLineColor: () => "#000",
          vLineColor: () => "#000",
        },
      },
    ],
    styles: {
      tableHeader: {
        bold: true,
        fontSize: 9,
        fillColor: "#f0f0f0",
        alignment: "center",
      },
    },
  };

  pdfMake.createPdf(docDefinition).open();
};
